"use client";

/**
 * Shared cart / buy / wishlist / download / express-sign-up plumbing used by
 * both `AssetPurchaseCard` (desktop sticky rail) and `MobilePurchaseBar`
 * (mobile bottom bar). Before this hook existed each component re-implemented
 * the same `handleAddToCart` / `handleBuyNow` / `handleToggleWishlist` /
 * `handleDownloadClick` / `handleConfirmDownload` flow verbatim — the mobile
 * bar even dropped a couple of platform-event logs and analytics calls in
 * subtle ways.
 *
 * One source of truth for:
 *   - "ensure profile row, idempotent cart insert" semantics
 *   - the wishlist toggle (through {@link useWishlistToggle})
 *   - download trigger via {@link useAssetDownload} (which itself owns the
 *     edge-function call, fingerprinting, fallback toasts, and the 7-asset
 *     daily-cap UX)
 *   - the express-sign-up dialog branching ("guest clicked X, prompt
 *     sign-up then run X")
 *   - the rating-prompt + tip dialog state both surfaces share
 *
 * Each consumer keeps its own JSX (the two layouts are intentionally
 * different) but routes every side-effect through the action callbacks this
 * hook returns.
 */
import { useCallback, useState } from "react";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { queryKeys } from "@/lib/query-keys";
import { useSession } from "@/contexts/SessionContext";
import { useProfile } from "@/contexts/ProfileContext";
import { useAssetAccess } from "@/hooks/useAssetAccess";
import { useAssetDownload } from "@/hooks/useAssetDownload";
import { useWishlistToggle } from "@/hooks/useWishlistToggle";
import { useAssetReviews } from "@/hooks/useAssetReviews";
import { useDailyDownloadCount } from "@/hooks/useDailyDownloadCount";
import { ensureCartItem } from "@/lib/cart-actions";
import { trackAddToCart } from "@/lib/analytics";
import { logEvent } from "@/lib/events";
import { REFERRAL_GOAL } from "@/lib/referrals";
import { recordFreeDownload, shouldShowConversionNudge, markConversionNudgeShown, shouldShowCreatorNudge, markCreatorNudgeShown, recordDailyFreeDownload, shouldShowSoftWall, markSoftWallShown } from "@/lib/conversion-nudge";
import type { LimitDialogVariant } from "@/lib/analytics/events";
import { wasRecentlyPrompted } from "@/components/asset-detail/RatingPromptDialog";
import { useRecommendedAssets } from "@/hooks/useRecommendedAssets";
import { allowsFreeClaim } from "@/lib/pwyw";
import type { PendingIntent } from "@/lib/pending-intent";
import type { AssetDetail } from "@/hooks/useAssetDetail";

export type PendingAction = "download" | "buy" | "cart" | "wishlist";

export interface UseAssetActionsOptions {
  asset: AssetDetail;
  /** Effective price (after sale) used by analytics. */
  effectivePrice?: number;
}

export function useAssetActions({
  asset,
  effectivePrice,
}: UseAssetActionsOptions) {
  const { user } = useSession();
  const { isAdmin } = useProfile();
  const router = useRouter();
  const queryClient = useQueryClient();

  // ── Status / mutation flags ──────────────────────────────────────────
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isBuyingNow, setIsBuyingNow] = useState(false);
  // The same cached per-asset status the page header's heart reads, so every
  // heart on the page agrees.
  const wishlist = useWishlistToggle({
    assetId: asset.id,
    assetTitle: asset.title,
    price: effectivePrice,
  });

  // ── Dialog state shared by both surfaces ─────────────────────────────
  const [showLicenseDialog, setShowLicenseDialog] = useState(false);
  const [licenseAgreed, setLicenseAgreed] = useState(false);
  const [showExpressSignUp, setShowExpressSignUp] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction>("download");
  // The pay-what-you-want amount a visitor chose before signing up, carried in
  // the cart/buy intent so the line resumes at that amount.
  const [pendingPwywAmount, setPendingPwywAmount] = useState<number | undefined>(undefined);
  const [showRatingPrompt, setShowRatingPrompt] = useState(false);
  const [showTipDialog, setShowTipDialog] = useState(false);
  const [showLimitBanner, setShowLimitBanner] = useState(false);
  /** Non-null when the limit/soft-wall paywall dialog should show. */
  const [limitDialogVariant, setLimitDialogVariant] = useState<LimitDialogVariant | null>(null);

  // ── Live data ────────────────────────────────────────────────────────
  // `is_free`, deliberately not `isFreeClaimable`: this flag means "downloadable
  // without choosing a price". A no-minimum PWYW listing can be taken for $0,
  // but only through its amount input, so it must keep `showPurchaseButtons`.
  // AssetDetail reads the same query key and passes the same value.
  const { data: access, isLoading: isLoadingAccess } = useAssetAccess(
    asset.id,
    asset.seller?.id,
    asset.is_free
  );
  const { data: reviews } = useAssetReviews(asset.id);
  const dailyDownload = useDailyDownloadCount();

  // Post-download recommendations. Fetched lazily — only once the post-download
  // dialog is actually about to show — so a plain asset-page view never pays for
  // it. Rendered inside RatingPromptDialog by both purchase surfaces.
  const { data: recommendations = [] } = useRecommendedAssets(asset.id, {
    userId: user?.id ?? null,
    limit: 3,
    enabled: showRatingPrompt,
  });

  const { downloadAsset, isDownloading } = useAssetDownload({
    onDownloadComplete: (assetId) => {
      if (!user) return;

      // Value-moment conversion nudge: a free-tier user just grabbed a free
      // asset (the exact behaviour of our Twitter-sourced cohort). Once they've
      // shown intent (≥2 free downloads) we surface the free path to unlimited
      // — earn a free week of Buyer Plus by inviting friends — at most once a day, and
      // it takes priority over the review prompt for that download.
      // A $0 take of a no-minimum PWYW listing spends the daily allowance just
      // like a free asset; a re-download of one the buyer paid for does not.
      const wasFreeClaim = asset.is_free || (allowsFreeClaim(asset) && !access?.hasPurchased);
      if (dailyDownload.isLimited && wasFreeClaim) {
        const total = recordFreeDownload();
        // Soft wall: after the Nth free download of the day, a dismissible
        // trial interstitial (once per day). Wins over the toast nudges — a
        // dialog and a toast fighting for the same moment reads as spam.
        const todayCount = recordDailyFreeDownload();
        if (shouldShowSoftWall(todayCount)) {
          markSoftWallShown();
          setLimitDialogVariant("soft_wall");
          return;
        }
        if (shouldShowConversionNudge(total)) {
          markConversionNudgeShown();
          toast("🎁 Unlock unlimited downloads — free", {
            description: `Invite ${REFERRAL_GOAL} friends and get a free week of Buyer Plus: unlimited downloads & free claims. Or go Plus anytime.`,
            action: { label: "Invite & earn", onClick: () => router.push("/dashboard/referrals") },
            duration: 10000,
          });
          return;
        }
        // Engaged free-downloader (≥4 grabs) — clearly an active UEFN dev. Pitch
        // the supply side once: the assets they download could be theirs to sell.
        if (shouldShowCreatorNudge(total)) {
          markCreatorNudgeShown();
          toast("You're clearly building in UEFN 👀", {
            description: "Other creators need assets like the ones you grab. List your own — free to list, keep most of every sale, fast payouts.",
            action: { label: "Become a creator", onClick: () => router.push("/sell") },
            duration: 11000,
          });
          return;
        }
      }

      // Only a holder can review (reviews INSERT policy): never the seller,
      // and never staff who downloaded a paid listing they don't hold.
      const holdsIt = !!access?.hasPurchased || wasFreeClaim;
      if (access?.isOwner || !holdsIt) return;
      const alreadyReviewed = reviews?.some((r) => r.user?.user_id === user.id);
      if (!alreadyReviewed && !wasRecentlyPrompted(assetId)) {
        setShowRatingPrompt(true);
      }
    },
    onLimitReached: () => {
      setShowLimitBanner(true);
      setLimitDialogVariant("limit");
    },
  });

  const openExpressSignUp = useCallback((action: PendingAction, pwywAmount?: number) => {
    setPendingPwywAmount(pwywAmount);
    setPendingAction(action);
    setShowExpressSignUp(true);
  }, []);

  const handleExpressSuccess = useCallback(() => {
    // The pending-intent resume (AssetDetail for downloads, the global
    // PendingIntentResumer for everything else) finishes the action — no
    // "click again" needed.
    toast.success("Welcome! You're signed in.");
  }, []);

  // ── Cart / buy / wishlist / download primitives ──────────────────────
  const addToCart = useCallback(
    async (overridePrice?: number) => {
      if (!user) { openExpressSignUp("cart", overridePrice); return; }
      setIsAddingToCart(true);
      try {
        const result = await ensureCartItem(user.id, asset.id, overridePrice);
        // ensureCartItem writes straight to Supabase, bypassing the React Query
        // cart cache — invalidate so the header badge and /cart reflect it.
        void queryClient.invalidateQueries({ queryKey: queryKeys.cart.all });
        if (result === "exists") { toast.info("Already in your cart"); return; }
        trackAddToCart({
          id: asset.id,
          title: asset.title,
          price: overridePrice ?? effectivePrice ?? asset.price,
          category: asset.category?.name,
        });
        toast.success("Added to cart");
      } catch (error) {
        console.error("Error adding to cart:", error);
        toast.error("Failed to add to cart. Please try again.");
      } finally {
        setIsAddingToCart(false);
      }
    },
    [user, asset.id, asset.title, asset.price, asset.category?.name, effectivePrice, openExpressSignUp, queryClient],
  );

  const buyNow = useCallback(
    async (overridePrice?: number) => {
      if (!user) { openExpressSignUp("buy", overridePrice); return; }
      setIsBuyingNow(true);
      try {
        await ensureCartItem(user.id, asset.id, overridePrice);
        // Invalidate before navigating so /cart mounts against fresh data
        // instead of a stale (staleTime 30 s) empty cart.
        void queryClient.invalidateQueries({ queryKey: queryKeys.cart.all });
        logEvent("buy_now_clicked", { asset_id: asset.id }, { severity: "high" });
        router.push("/cart");
      } catch (error) {
        console.error("Error with buy now:", error);
        toast.error("Something went wrong. Please try again.");
      } finally {
        setIsBuyingNow(false);
      }
    },
    [user, asset.id, openExpressSignUp, router, queryClient],
  );

  const toggleWishlist = useCallback(() => {
    if (!user) { openExpressSignUp("wishlist"); return; }
    wishlist.toggle();
  }, [user, openExpressSignUp, wishlist]);

  const openDownloadDialog = useCallback(() => {
    if (!user) { openExpressSignUp("download"); return; }
    // At the daily cap the click becomes the conversion moment: show the
    // trial paywall instead of a dead disabled button. Only for a download
    // that would be a free claim — the seller, staff and buyers re-downloading
    // what they hold aren't stopped by the free allowance here.
    const wouldClaimFree = asset.is_free || (allowsFreeClaim(asset) && !access?.hasPurchased);
    if (dailyDownload.isAtLimit && wouldClaimFree && !access?.isOwner && !isAdmin) {
      setLimitDialogVariant("limit");
      return;
    }
    setLicenseAgreed(false);
    logEvent("license_dialog_opened", { asset_id: asset.id }, { severity: "low" });
    setShowLicenseDialog(true);
  }, [user, asset, access?.hasPurchased, access?.isOwner, isAdmin, openExpressSignUp, dailyDownload.isAtLimit]);

  // The download itself reports (analytics + event) once it has succeeded —
  // logging here as well double-counted every download and counted failures.
  const confirmDownload = useCallback(async () => {
    setShowLicenseDialog(false);
    await downloadAsset(asset.id, undefined, asset.title);
  }, [asset.id, asset.title, downloadAsset]);

  const requestTip = useCallback(() => {
    setShowTipDialog(true);
  }, []);

  // What the sign-up dialog saves for the resume: the action, plus the chosen
  // pay-what-you-want amount for a cart/buy, so it isn't lost at sign-up.
  const assetRef = { assetId: asset.id, assetSlug: asset.slug, assetTitle: asset.title };
  const signUpIntent: PendingIntent =
    pendingAction === "cart" || pendingAction === "buy"
      ? { kind: pendingAction, ...assetRef, pwywAmount: pendingPwywAmount }
      : { kind: pendingAction, ...assetRef };

  // Convenience flags both surfaces compute identically.
  const showDownloadButton = (access?.hasPurchased || access?.isOwner) && !asset.is_free;
  const showFreeDownloadButton = asset.is_free && (access?.canDownload || access?.isOwner);
  const showPurchaseButtons = !access?.canDownload && !asset.is_free && !access?.isOwner;
  // Tips come from signed-in fans who hold the listing — never its seller
  // (create-tip refuses that too), never a visitor.
  const showTipButton =
    !!user &&
    asset.tipping_enabled &&
    !access?.isOwner &&
    (access?.hasPurchased || (asset.is_free && access?.canDownload));

  return {
    // status
    access,
    isLoadingAccess,
    isAddingToCart,
    isBuyingNow,
    isDownloading,
    isInWishlist: wishlist.isInWishlist,
    isTogglingWishlist: wishlist.isToggling,
    isProcessing: isAddingToCart || isBuyingNow || isDownloading,
    // computed
    showDownloadButton,
    showFreeDownloadButton,
    showPurchaseButtons,
    showTipButton,
    // actions
    addToCart,
    buyNow,
    toggleWishlist,
    openDownloadDialog,
    confirmDownload,
    requestTip,
    openExpressSignUp,
    handleExpressSuccess,
    // dialog/banner state
    showLicenseDialog,
    setShowLicenseDialog,
    licenseAgreed,
    setLicenseAgreed,
    showExpressSignUp,
    setShowExpressSignUp,
    signUpIntent,
    showRatingPrompt,
    setShowRatingPrompt,
    recommendations,
    showTipDialog,
    setShowTipDialog,
    showLimitBanner,
    limitDialogVariant,
    setLimitDialogVariant,
    dailyDownload,
  };
}
